
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { toast } from 'components/Shared/Toast';
import { useTranslation } from 'react-i18next';
import { useProfileState, useProfileAbility } from 'context/profile';
import Whoami from 'actions/whoami-react';
import { templateAction } from 'api/actions/templates';
import { formAction } from 'api/actions/forms';
import { fetcher } from 'api/client'
import { useQueryClient, useMutation } from 'react-query';
import { downloadFile } from 'utils';
import bootbox from 'components/Modals/Bootbox';
import { useHistory } from 'react-router-dom';

function useTemplateActions(template, options = {}) {
  const history = useHistory();
  const { profile, roles } = useProfileState();
  const queryClient = useQueryClient();
  const profileAbility = useProfileAbility();
  const { t } = useTranslation(['translation', 'validations', 'glossary', 'common'])

  const { mutate: doTemplateAction } = useMutation((data) => fetcher(templateAction(template._id, data.action, data.payload, data.config)), {
    onError: (error) => {
      toast.error(t(`errors.${error.message}`));
    }
  })

  const { mutate: doFormAction } = useMutation((data) => fetcher(formAction(data.formId, data.payload, data.params)), {
    onError: (error) => {
      toast.error(t(`errors.${error.message}`));
    }
  });

  const actionList = useMemo(() => {
    const whoami = new Whoami(template, profile.email, profile._id, roles, profile.organization, profile.groups);

    const actionListObj = {
      EDIT: {
        faIcon: 'pen',
        label: t('contracts.options.edit'),
        shouldShow: () => {
          if (whoami.isFolder()) return false;
          if (whoami.isArchived()) return false;
          return true;
        },
        onClick: () => {
          history.push(`/templates/editor/${template._id}`);
        }
      },
      RENAME: {
        faIcon: 'pen-square',
        label: t('contracts.options.rename'),
        shouldShow: () => {
          if (!whoami.isOwner()) return false;
          return true;
        },
        onClick: async () => {
          const oldName = template.name;
          const newName = await bootbox.prompt({
            title: t('contracts.options.rename_name'),
            value: oldName
          });
          if (newName === oldName) return;
          if (!newName) return;

          const payload = {
            name: newName
          }

          doTemplateAction({ action: 'rename', payload }, {
            onSuccess: () => {
              queryClient.invalidateQueries('templates-all');
              toast.success(t('validations:messages.successfullyUpdated', { context: 'male', entity: t('glossary:contract') }));
            }
          });
        }
      },
      DUPLICATE: {
        faIcon: 'copy',
        label: t('templates.options.duplicate'),
        shouldShow: () => {
          return true;
        },
        onClick: async () => {
          doTemplateAction({ action: 'duplicate' }, {
            onSuccess: () => {
              toast.success(t('wordList.success_duplicated', { context: 'male', entity: t('glossary:contract') }));
              queryClient.invalidateQueries('templates-all');
            }
          })
        }
      },
      MASSIVE_TEMPLATE: {
        faIcon: 'file-excel',
        label: t('templates.options.massive_template'),
        shouldShow: () => {
          return true;
        },
        onClick: async () => {
          doTemplateAction({ action: 'massive', config: { responseType: 'blob' } }, {
            onSuccess: (data) => {
              downloadFile({
                name: `${template.name}`,
                blob: new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
                ext: 'xlsx'
              })();
            }
          });
        }
      },
      // ARCHIVE: {
      //   faIcon: 'archive',
      //   label: t('templates.options.archive'),
      //   shouldShow: () => {
      //     if (!whoami.isOwner()) return false;
      //     if (!whoami.isArchived()) return true;
      //   },
      //   onClick: () => {
      //   }
      // },
      DELETE: {
        faIcon: 'trash',
        label: t('templates.options.delete'),
        shouldShow: () => {
          if (!whoami.isOwner()) return false;
          return true;
        },
        onClick: async () => {
          if (await bootbox.confirm(t('templates.options.delete_confirm'))) {
            doTemplateAction({ action: 'delete' }, {
              onSuccess: () => {
                toast.success(t('templates.listing.delete_success', { context: 'male', entity: t('glossary:contract') }));
                queryClient.invalidateQueries('templates-all');
              }
            });
          }
        }
      },
      PUBLISH: {
        faIcon: 'upload',
        label: t('templates.options.publish'),
        shouldShow: () => {
          if (whoami.isArchived()) return false;
          if (!whoami.hasQuestionare()) return false;
          // if (!whoami.participantsCountEqualOrLessThan(2)) return false;
          if (whoami.hasForm()) return false;
          return true;
        },
        onClick: async () => {
          const options = [
            {
              label: t('templates.options.publish_fill_participant'),
              value: '',
            },
            ...template.participants.map(participant => (
              { value: participant._id, label: participant.label }
            )),
          ]
          const participantId = await bootbox.prompt({
            title: t('templates.options.publish_confirm'),
            message: t('templates.options.publish_fill_participant'),
            buttons: {
              cancel: { label: 'Cancelar', },
              confirm: { label: 'Aceptar', },
            },
            inputType: 'select',
            inputOptions: options,
           
          })

          if (!participantId) return;

          doFormAction({ payload: { templateId: template._id, participantId } }, {
            onSuccess: () => {
              queryClient.invalidateQueries('templates-all');
              toast.success(t('templates.options.publish_success'))
            }
          });
        }
      },
      UNPUBLISH: {
        faIcon: 'ban',
        label: t('templates.options.unpublish'),
        shouldShow: () => {
          if (!whoami.hasForm()) return false;
          return true;
        },
        async onClick() {
          if (await bootbox.confirm({
            message: t('templates.options.unpublish_confirm'),
            buttons: {
              cancel: { label: 'Cancelar', },
              confirm: { label: 'Aceptar', },
            }
          })) {
            const form = whoami.getUserForm();
            const formId = form._id
            doFormAction({ formId, payload: {}, params: { method: 'DELETE' } }, {
              onSuccess: () => {
                queryClient.invalidateQueries('templates-all');
              }
            })
          }
        }
      },
      // UNARCHIVE: {
      //   faIcon: 'check',
      //   label: t('templates.options.activate'),
      //   shouldShow: () => {
      //     if (whoami.isArchived()) return true;
      //   },
      //   onClick: () => {
      //   }
      // },
      OPEN_FORM: {
        faIcon: 'fab fa-wpforms',
        label: t('templates.options.open_form'),
        shouldShow: () => {
          if (whoami.hasForm()) return true;
        },
        onClick() {
          const userForm = whoami.getUserForm();
          if (userForm._id) {
            window.open(`${process.env.REACT_APP_STARTER_URL}/forms/${userForm._id}`, '_blank');
          }
        }
      },
      SHARE_TEMPLATE: {
        label: t('templates.options.share'),
        faIcon: 'share-alt',
        shouldShow: () => {
          if (whoami.isTemplateTaken()) return false;
          if (whoami.isTemplateShareToClient()) return false;
          if (whoami.roleCan('SHARE_TEMPLATE_BETWEEN_TENANTS') && whoami.isOwner()) return true;
        },
        onClick: async () => {
          const email = await bootbox.prompt(t('templates.options.share_email'));
          if (!email) return;

          doTemplateAction({ action: 'sharebetweentenants', payload: { email } }, {
            onSuccess: () => {
              toast.success(t('templates.options.share_success'));
            }
          });

        }
      },
    };

    const filteredActions = Object.fromEntries(Object.entries(actionListObj).filter?.(([key, action]) => action?.shouldShow?.()).map?.(([key, action]) => ([key, { ...action, name: key }]))) ?? {}

    if (options.returnAsObject) {
      return filteredActions
    }

    return Object.values(filteredActions)
  }, [template, profile.email, profile._id, profile.organization, profile.groups, roles, t, options.returnAsObject, doTemplateAction, doFormAction]);

  return actionList;
}

export default useTemplateActions;